import { type ReactNode, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { SlideOver } from '@/components/SlideOver';
import { useToast } from '@/components/ToastProvider';
import { ModelUploadForm } from '@/features/models';
import { CreateCollectionForm } from '@/features/collections/components';
import { CreateJobForm } from '@/features/jobs/components';
import { useCreateJob } from '@/features/jobs/hooks';
import { useUIStore } from '@/stores/ui';
import { useUIPreferences } from '@/stores/useUIPreferences';
import { useAuthStore } from '@/stores/auth';
import { websocketService } from '@/lib/websocket';
import type { CreateSpoolInput, CreateJobInput } from '@maker-toolchest/shared';
import { SpoolForm, useCreateSpool } from '@/features/spools';

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const activeModal = useUIStore((state) => state.activeModal);
  const closeModal = useUIStore((state) => state.closeModal);
  const toast = useToast();
  const [createJobError, setCreateJobError] = useState<string | null>(null);
  const createSpoolMutation = useCreateSpool();
  const createJobMutation = useCreateJob();

  // Initialize and sync UI preferences with database
  useUIPreferences();

  const handleCreateSpool = async (formData: CreateSpoolInput) => {
    try {
      await createSpoolMutation.mutateAsync(formData);
      closeModal();
    } catch {
      // Error handled by mutation
    }
  };

  const handleCreateJob = async (formData: CreateJobInput) => {
    try {
      setCreateJobError(null);
      await createJobMutation.mutateAsync(formData);
      closeAddJobSidebar();
      toast.success('Job created successfully');
    } catch (error: any) {
      setCreateJobError(error.message || 'Failed to create job');
    }
  };

  const queryClient = useQueryClient();
  const {
    isAddModelSidebarOpen,
    addModelSidebarData,
    closeAddModelSidebar,
    isAddJobSidebarOpen,
    addJobSidebarData,
    closeAddJobSidebar,
    isCollectionSidebarOpen,
    closeCollectionSidebar,
    collectionSidebarData,
  } = useUIStore();
  const { currentTenant } = useAuthStore();

  // Initialize WebSocket connection for real-time updates
  useEffect(() => {
    // Set up connection failure callback to notify user
    websocketService.setOnConnectionFailure(() => {
      toast.warning(
        'Real-time updates are temporarily unavailable. Your changes are still being saved.'
      );
    });

    // Connect when component mounts and user is authenticated
    websocketService.connect(queryClient);

    // Cleanup: disconnect when component unmounts or tenant changes
    return () => {
      websocketService.disconnect();
    };
  }, [queryClient, currentTenant?.tenantId, toast]);

  // Clear modal data when navigating to prevent stale state
  // For example, if a modal was opened with preselectedFilamentId from one page,
  // navigating away should clear that state so it doesn't persist when the modal
  // is opened again from a different context
  useEffect(() => {
    if (activeModal) {
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleUploadSuccess = (modelId: string) => {
    // Call the callback if provided
    if (addModelSidebarData?.onSuccess) {
      addModelSidebarData.onSuccess(modelId);
    }
    closeAddModelSidebar();
  };

  // Determine collection sidebar title
  let collectionSidebarTitle = 'Create Collection';
  if (collectionSidebarData?.collection) {
    collectionSidebarTitle = 'Edit Collection';
  } else if (collectionSidebarData?.parentCollectionName) {
    collectionSidebarTitle = `Add Sub-Collection to ${collectionSidebarData.parentCollectionName}`;
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* Header - full width */}
      <Header />

      {/* Main area with sidebar and content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar />

        {/* Content area */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Global Add Spool Form */}
      <SpoolForm
        open={activeModal === 'add-spool'}
        onClose={closeModal}
        onSubmit={handleCreateSpool}
        loading={createSpoolMutation.isPending}
      />

      {/* Global Add Model Sidebar */}
      <SlideOver
        open={isAddModelSidebarOpen}
        onClose={closeAddModelSidebar}
        title="Add Model"
        description="Upload an STL file or add a model from an external site"
      >
        <ModelUploadForm onSuccess={handleUploadSuccess} />
      </SlideOver>

      {/* Global Collection Sidebar */}
      <SlideOver
        open={isCollectionSidebarOpen}
        onClose={closeCollectionSidebar}
        title={collectionSidebarTitle}
        description={
          collectionSidebarData?.collection
            ? 'Update collection details'
            : 'Organize your 3D models into collections'
        }
      >
        <CreateCollectionForm
          collection={collectionSidebarData?.collection}
          defaultParentId={collectionSidebarData?.defaultParentId}
          parentCollectionName={collectionSidebarData?.parentCollectionName}
        />
      </SlideOver>

      {/* Global Add Job Sidebar */}
      <CreateJobForm
        open={isAddJobSidebarOpen}
        onClose={() => {
          closeAddJobSidebar();
          setCreateJobError(null);
        }}
        onSubmit={handleCreateJob}
        loading={createJobMutation.isPending}
        error={createJobError}
        defaultModelId={addJobSidebarData?.defaultModelId}
      />
    </div>
  );
}
